import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import Button from 'components/Button/Button';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import 'theme/Forms.scss';
import colors from 'theme/Colors.module.scss';

import {
  Flex,
  Text,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';

import { useSendPasswordResetEmailMutation } from 'modules/app/appApi';

const defaults = { resetEmail: '' };
const schema = Yup.object().shape({
  resetEmail: Yup.string()
    .email('Invalid email.')
    .required('Email is required.')
    .trim(),
});

const ResetPassword = props => {
  const dispatch = useDispatch();
  const [emailSent, setEmailSent] = useState(false);

  const [sendPasswordResetEmail] = useSendPasswordResetEmailMutation();

  useEffect(() => {
    emailSent && setEmailSent(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values, form) => {
    form.setSubmitting(true);
    try {
      await sendPasswordResetEmail({ email: values.resetEmail }).unwrap();

      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Reset password email send successfully',
        })
      );

      form.resetForm();
      !emailSent && setEmailSent(true);
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Faild to sent reset password email',
        })
      );
    } finally {
      form.setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={defaults}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {form => {
          const { isSubmitting } = form;

          return (
            <Form className="authForm">
              <Text as="p" className="title">
                Reset your Password
              </Text>

              <Flex flexDirection="column" marginY="2rem" gap="2rem">
                <Field name="resetEmail">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.resetEmail && form.touched.resetEmail
                      }
                    >
                      <FormLabel>Email</FormLabel>
                      <Input
                        {...field}
                        placeholder="Email*"
                        focusBorderColor={colors.primary600}
                        backgroundColor={colors.white}
                        disabled={isSubmitting}
                        onFocus={() => emailSent && setEmailSent(false)}
                      />
                      <FormErrorMessage>
                        {form.errors.resetEmail}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {emailSent && (
                  <Text as="p" textColor={colors.status00}>
                    <strong>Please check your email to continue</strong>.
                  </Text>
                )}
              </Flex>

              <Button
                type="submit"
                text="Reset my Password"
                textColor={colors.white}
                btnStyle="solid"
                hoverColor={colors.primary500}
                textHoverColor={colors.white}
                disabled={isSubmitting}
              />

              <Text as="p" className="loginOptions">
                <Link to="/login">Log in</Link>.
              </Text>

              <Text as="p" className="footnote">
                An asterisk (*) marks a required field.
              </Text>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default ResetPassword;
